/**
 * Login Modal Component
 *
 * Modal-based authentication interface that replaces the full-page login flow.
 * Preserves user context by showing login form as an overlay.
 *
 * Architecture: Uses Layer 2 semantic tokens (--oro-*, --text-*) only.
 * Pattern: Follows existing modal patterns from AdminInviteModal.tsx
 *
 * Issue #830: Redirect URL stored in D1 (not sessionStorage) for cross-platform
 * compatibility (React Native/Expo). Magic link opens in new tab/browser, so
 * sessionStorage (tab-scoped) doesn't work.
 *
 * Issue #1328: SMS login support with auto-detect email vs phone.
 *
 * Terms consent (#3262, Apple Guideline 1.2 via #3257 F4): the line under the
 * form links Terms and Privacy and PRESENTS the agreement before sign-in, in
 * every non-loading sub-state (idle, SMS code, reviewer). Presented only —
 * the recorded-acceptance artifact remains #2996 (blocked by #2928); do not
 * read this line as satisfying it.
 *
 * NOTE (#2853): this component still does NOT render a TCR consent block. The
 * compliant block is `SmsConsentDisclosure`, whose only consumer is
 * `AdminInviteModal` — i.e. it appears when a leader types someone ELSE's
 * number, never when a user types their own here. Closing that gap is tracked
 * on epic #2936; the #3262 terms line is an EULA presentation, not TCR
 * consent, and does not close it.
 */

import { useState, useEffect, useCallback, useRef, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';
import Button from './Button';
import Input from './Input';
import Card from './Card';
import LegalDocModal, { type LegalDocSlug } from './LegalDocModal';
import { authApi, type ApiError } from '../lib/api';
import { invitesApi } from '../lib/api/invites';
import { useAuth } from '../contexts/AuthContext';
import { getUserFacingError } from '../lib/errorMessages';
import { smsOptedOutRecoveryCopy } from '../lib/smsRecovery';
import {
  normalizePhoneE164,
  isPhoneNumber,
  isMalformedPhoneAttempt,
  LOGIN_IDENTIFIER_ERROR,
} from '../lib/phone';
import { hasAcceptedTerms, markTermsAccepted } from '../lib/termsAcceptance';
import { handOffPendingNavigation } from '../lib/pendingNavigation';
import { toSafeRedirectPath } from '../lib/safeRedirect';
import { adoptLogin } from '../lib/accountGuard';
import EnvironmentBanner from './EnvironmentBanner';
import OroLockup from './OroLockup';

// STOP-locked recovery copy (#2930) now lives in `../lib/smsRecovery` so web and
// native share one LEAD + ™ cadence. This component consumes `smsOptedOutRecoveryCopy`
// (imported above); the `SMS_OPTED_OUT_RECOVERY_LEAD` constant is asserted directly
// from that shared module in the tests.

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  pendingNavigation?: string | null;
}

// #2441: `isPhoneNumber` (phone-vs-email detection) now lives in the shared
// `../lib/phone` module (`@shared/lib/phone` for native), so web and native
// route on ONE detector instead of a verbatim copy each. `isMalformedPhoneAttempt`
// (imported alongside) flags a digit string that is clearly a phone but invalid,
// and `LOGIN_IDENTIFIER_ERROR` is the one shared message for it.

// #2557: mask an E.164 phone for display — keep the +1 and the last four,
// bullet the middle. Same convention as the verified-number display in
// ProfileSettingsPage (that one is page-local; the layer boundary forbids a
// component importing from pages, so the small helper is duplicated here).
function maskPhoneE164(phone: string): string {
  if (!phone || phone.length < 6) return phone;
  return `${phone.slice(0, 2)}•••••${phone.slice(-4)}`;
}


function currentPath(): string {
  return window.location.pathname + window.location.search;
}

export default function LoginModal({
  isOpen,
  onClose,
  onSuccess,
  pendingNavigation
}: LoginModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [identifier, setIdentifier] = useState(''); // email or phone
  const [loading, setLoading] = useState(false);
  // #3312: the Terms checkbox. Read ONCE on mount — the value must not change
  // while the modal is open, or the control could vanish under the user
  // mid-interaction. Presence-based, so bumping the legal version does not
  // re-prompt (owner requirement: once per device install).
  const [needsTerms] = useState(() => !hasAcceptedTerms());
  const [termsChecked, setTermsChecked] = useState(false);
  // Every submit path is blocked until the box is ticked, on every sub-state.
  const termsBlocked = needsTerms && !termsChecked;
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState(false);
  // SMS verification (#1453)
  const [awaitingCode, setAwaitingCode] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [canResend, setCanResend] = useState(false);
  const [normalizedPhone, setNormalizedPhone] = useState(''); // E.164 for API calls
  // #2345: App Review demo-account static-code entry (email path only; shown
  // when /auth/magic-link/request returns reviewer_code_entry for this email).
  const [reviewerMode, setReviewerMode] = useState(false);
  const [reviewerCode, setReviewerCode] = useState('');
  // #3324: the Terms/Privacy links open the document in a dialog over this
  // modal instead of navigating away, so the identifier and Terms-checkbox
  // state (both held here) survive being read. `null` = closed.
  const [legalDoc, setLegalDoc] = useState<LegalDocSlug | null>(null);

  // #3264 Codex r3 P2: auth-attempt generation, bumped by the close-reset
  // effect. Every async handler captures the current value before its await
  // and re-checks it before any setState in the continuation, so a response
  // that resolves AFTER the modal closed is INERT — nothing restores
  // awaitingCode/success/error onto the reset (or reopened) modal. This is
  // the "close-during-pending is safe" axis: a close can still race an
  // in-flight request through the parent — the isAuthenticated auto-close
  // effect (handleAuthSuccess -> onClose once a verify seeds the session) or
  // the parent setting isOpen=false — and the late response must not restore
  // stale UI state onto the reset modal. (#3324: opening a legal document no
  // longer closes this modal — the legal links present the doc in a dialog
  // over this surface, which stays mounted — so that close path is gone; the
  // guard still holds for the parent/auto-close paths above.) A ref, not
  // state: the check must be synchronous inside the continuation, immune to
  // the closure staleness setState guards have.
  //
  // #3264 Codex r4 P2 boundary: the guard gates UI-STATE transitions ONLY
  // (navigate, onClose/onSuccess, awaitingCode/success/loading/error). It
  // must NOT gate query-cache writes that record SERVER FACTS — once
  // verify/reviewer-login resolves, the session cookie exists whether or not
  // the modal is still open, so setQueryData(['me'], …) + the auth
  // invalidations run unconditionally on success. Guarding them left a
  // cached guest ['me'] (5-min staleTime) masking a real session when the
  // user closed via a legal link mid-verify.
  const attemptRef = useRef(0);

  // Get Turnstile site key from environment (optional - graceful degradation)
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

  // Detect if user is entering a phone number
  const isPhone = isPhoneNumber(identifier);

  const handleAuthSuccess = useCallback(() => {
    if (onSuccess) {
      onSuccess();
    }
    
    // Navigate to pending destination or close modal (#3544: app-relative only)
    const destination = toSafeRedirectPath(pendingNavigation);
    if (destination) {
      navigate(destination);
    }
    
    onClose();
  }, [onSuccess, pendingNavigation, navigate, onClose]);

  const handleClose = useCallback(() => {
    if (!loading) {
      onClose();
    }
  }, [loading, onClose]);

  // #3324: a cross-link INSIDE a legal doc that is not terms/privacy (contact,
  // settings, SMS help). Terms<->privacy are handled in place by LegalDocModal
  // and never reach here. These genuinely leave the login flow, so close both
  // the legal dialog and this modal before routing — otherwise this z-50
  // overlay would stay parked over the destination page.
  const handleLegalNavigateAway = useCallback(
    (href: string) => {
      setLegalDoc(null);
      onClose();
      // #3324 (George F9): only follow first-party in-app paths. ContentDocView
      // routes mailto:/tel:/http(s) itself and never calls this; a
      // protocol-relative `//host` or an odd scheme reaching here must NOT be
      // handed to navigate(). The docs are first-party, so anything else is
      // ignored silently.
      if (href.startsWith('/') && !href.startsWith('//')) {
        navigate(href);
      }
    },
    [navigate, onClose],
  );

  // Close modal if user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && isOpen) {
      handleAuthSuccess();
    }
  }, [isAuthenticated, isOpen, handleAuthSuccess]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Invalidate in-flight requests FIRST (see attemptRef above), then
      // reset. loading is reset here too — its finally-side reset is
      // generation-guarded, so without this line a close mid-request would
      // leave the reopened modal stuck disabled.
      attemptRef.current += 1;
      setLoading(false);
      setIdentifier('');
      setError(null);
      setSuccess(false);
      setTurnstileToken(null);
      setTurnstileError(false);
      setAwaitingCode(false);
      setVerificationCode('');
      setCanResend(false);
      setNormalizedPhone('');
      setReviewerMode(false);
      setReviewerCode('');
      // #3324: a stale legal dialog must not survive a close/reopen of login.
      setLegalDoc(null);
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // #3324: while a legal document dialog is open on top, Escape belongs
        // to that dialog (it closes it via its own listener). Don't also close
        // the login modal underneath.
        if (legalDoc) return;
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [isOpen, handleClose, legalDoc]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Client-side validation
    if (!identifier.trim()) {
      setError('Please enter an email address or phone number');
      return;
    }

    // #3312: belt to the disabled button's braces. The button is disabled, but
    // a form can also be submitted with Enter, so the guard lives on the
    // handler too — this is the one that actually holds.
    if (termsBlocked) {
      setError('Please agree to the Terms of Service and Privacy Policy to continue.');
      return;
    }

    // Normalize phone to E.164 upfront — used for all SMS API calls
    let phoneE164 = '';
    if (isPhone) {
      const normalized = normalizePhoneE164(identifier);
      if (!normalized) {
        setError('Please enter a valid US or Canada phone number');
        return;
      }
      phoneE164 = normalized;
      setNormalizedPhone(normalized); // Persist for verify/resend
    } else {
      // #2441: a digit string that is clearly a phone attempt but not a valid
      // US/CA number would otherwise fall through to the email check below and
      // surface the generic "valid email address" error — confusing when the
      // user obviously typed a phone. (On native, the same input hits the email
      // endpoint and gets the backend's raw "Invalid request data".) Catch it
      // here, before any request, with one shared friendly message.
      if (isMalformedPhoneAttempt(identifier)) {
        setError(LOGIN_IDENTIFIER_ERROR);
        return;
      }
      // Email validation
      if (!identifier.includes('@')) {
        setError('Please enter a valid email address');
        return;
      }
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    // #3264 Codex r3 P2: snapshot the generation; a close bumps it.
    const attempt = attemptRef.current;

    try {
      // Extract invite code from pendingNavigation if it's an invite URL
      let inviteCode: string | undefined;
      if (pendingNavigation?.startsWith('/invite/')) {
        inviteCode = pendingNavigation.split('/invite/')[1]?.split('?')[0];
      }

      if (isPhone) {
        // SMS login flow (#1453) — send verification code via TextGrid
        await authApi.requestSmsCode(
          phoneE164,
          turnstileToken || undefined,
          inviteCode,
          toSafeRedirectPath(pendingNavigation) ?? undefined,
          needsTerms && termsChecked
        );
        // Modal closed while the request was in flight — the close-reset
        // already cleared normalizedPhone; setting awaitingCode here would
        // reopen onto a verification form that submits an empty phone.
        if (attemptRef.current !== attempt) return;
        // Show code input instead of generic success
        setAwaitingCode(true);
        setLoading(false);
        // Enable resend after 30 seconds
        setTimeout(() => {
          if (attemptRef.current === attempt) setCanResend(true);
        }, 30000);
        return; // Don't set success (that shows "check your inbox" message)
      } else {
        // Email magic link flow
        const resp = await authApi.requestMagicLink(
          identifier,
          turnstileToken || undefined,
          inviteCode,
          toSafeRedirectPath(pendingNavigation) ?? undefined,
          needsTerms && termsChecked
        );
        // Closed mid-flight: don't restore a stale success/reviewer state.
        if (attemptRef.current !== attempt) return;
        // #2345: armed App Review demo account — collect the static review code
        // instead of "check your email" (the reviewer can't read our inbox).
        if (resp?.reviewer_code_entry) {
          setReviewerMode(true);
          setLoading(false);
          return; // Don't set success (that shows "check your inbox")
        }
      }
      setSuccess(true);
      // Reset Turnstile widget after successful submission
      setTurnstileToken(null);
    } catch (err) {
      if (attemptRef.current !== attempt) return; // closed mid-flight (#3264 P2)
      const apiError = err as ApiError;

      // Handle bot detection error
      if (apiError.code === 'BOT_CHECK_FAILED') {
        setError('Security check failed. Please refresh the page and try again.');
        setTurnstileError(true);
        setLoading(false);
        return;
      }

      // Handle environment whitelist restriction (#1447) — pass through backend message
      if (apiError.code === 'FORBIDDEN') {
        setError(apiError.message || 'This is a test environment. To sign up for ORO move\u2122, visit oro.fit');
        setLoading(false);
        return;
      }

      // #2930: a STOP-suppressed phone cannot receive a login code, so the
      // generic wrapper copy is a dead end. Lead with the shipped add-email
      // recovery path (reachable from a live session) and KEEP the backend's
      // own message, which carries the actionable "reply START to <number>"
      // target — the phone-only user's only self-serve door.
      if (apiError.code === 'SMS_OPTED_OUT') {
        setError(smsOptedOutRecoveryCopy(apiError));
        setLoading(false);
        return;
      }

      // Handle invite-required error - auto-submit to waitlist (email only)
      if (apiError.code === 'INVITE_REQUIRED' && !isPhone) {
        try {
          await invitesApi.joinWaitlist({
            email: identifier,
            source: 'blocked_signup',
          });
          if (attemptRef.current !== attempt) return; // closed mid-waitlist
          setSuccess(true); // Show same success message
        } catch {
          if (attemptRef.current !== attempt) return;
          setError('Unable to join waitlist. Please try again.');
        } finally {
          if (attemptRef.current === attempt) setLoading(false);
        }
        return;
      }

      setError(getUserFacingError(apiError.code, apiError.message));
    } finally {
      // Guarded: the close-reset already set loading=false for the next open;
      // an unguarded write here would be harmless today but is kept symmetric
      // with every other continuation write.
      if (attemptRef.current === attempt) setLoading(false);
    }
  };

  // SMS code verification handler (#1453)
  const handleVerifyCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    const attempt = attemptRef.current; // #3264 P2

    try {
      const response = await authApi.verifySmsCode(normalizedPhone, verificationCode);

      // #3312: mark the device only once the login actually COMPLETED. Marking
      // at request time would suppress the box for someone who ticked it and
      // then abandoned the flow, leaving them permanently unrecorded.
      markTermsAccepted();

      // Set user data directly in cache (MagicLoginPage pattern).
      // UNCONDITIONALLY — no generation check first (#3264 Codex r4 P2): the
      // session cookie is already set server-side, so these writes record a
      // server fact. A close mid-verify must not skip them, or the cached
      // guest ['me'] (5-min staleTime) keeps AuthContext signed-out over a
      // real session (the legal routes disable useMe, so nothing refetches).
      // A new account is covered by OnboardingModal, which picks its own
      // destination when it finishes. Hand it the destination, as
      // MagicLoginPage does for the email link, or "Set up your next event"
      // ends on the club page (#3517; Frank/George/Codex on PR #3530). Same
      // precedence as the navigation below: the server's redirect_url is the
      // echo of the pendingNavigation this modal sent with the code request
      // (backend auth.ts stores it and returns it on verify).
      // #3544: the server echo is untrusted; an unsafe value is dropped. The
      // modal's own destination goes through the same check.
      const serverRedirect = toSafeRedirectPath(response.redirect_url);
      const localRedirect = toSafeRedirectPath(pendingNavigation);
      handOffPendingNavigation(
        serverRedirect || localRedirect,
        !response.user.onboarding_completed,
      );

      // #3881: a different account than this tab last held loads fresh, even if
      // the modal closed mid-verify. sessionStorage carries the handoff above.
      if (adoptLogin(response.user.id, serverRedirect || localRedirect || currentPath())) return;

      queryClient.setQueryData(['me'], response.user);
      await queryClient.invalidateQueries({ queryKey: ['me'], refetchType: 'none' });
      await queryClient.invalidateQueries({ queryKey: ['event'], refetchType: 'none' });

      // Closed anywhere between submit and here (#3264 P2 + George r2 F2):
      // login succeeded and the cache is seeded — skip the UI tail
      // (navigate/onSuccess/onClose) so a user who clicked a legal link
      // isn't yanked off it; AuthContext converges on its own.
      if (attemptRef.current !== attempt) return;

      // Navigate to redirect or pending destination
      if (serverRedirect) {
        navigate(serverRedirect);
      } else if (localRedirect) {
        navigate(localRedirect);
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      if (attemptRef.current !== attempt) return; // closed mid-flight (#3264 P2)
      const apiError = err as ApiError;
      setError(apiError.message || 'Invalid code. Please try again.');
    } finally {
      if (attemptRef.current === attempt) setLoading(false);
    }
  };

  // #2345: App Review demo-account static-code verify. Mirrors handleVerifyCode's
  // post-login flow (cache seed + navigate); the backend gate does the real work.
  const handleReviewerLogin = async (e: FormEvent) => {
    e.preventDefault();
    if (!reviewerCode.trim()) {
      setError('Please enter the review code');
      return;
    }

    setLoading(true);
    setError(null);

    const attempt = attemptRef.current; // #3264 P2

    try {
      const response = await authApi.reviewerLogin(
        identifier,
        reviewerCode.trim(),
        needsTerms && termsChecked
      );

      // #3312: see handleVerifyCode — mark on completion, not on request.
      markTermsAccepted();

      // Server-fact cache writes run unguarded (#3264 Codex r4 P2) — see
      // handleVerifyCode: the cookie is set even if the modal closed.
      const serverRedirect = toSafeRedirectPath(response.redirect_url); // #3544
      const localRedirect = toSafeRedirectPath(pendingNavigation);
      // #3881: see handleVerifyCode.
      if (adoptLogin(response.user.id, serverRedirect || localRedirect || currentPath())) return;

      queryClient.setQueryData(['me'], response.user);
      await queryClient.invalidateQueries({ queryKey: ['me'], refetchType: 'none' });
      await queryClient.invalidateQueries({ queryKey: ['event'], refetchType: 'none' });

      // Closed anywhere since submit (#3264 P2 + George r2 F2) — see
      // handleVerifyCode: cache is seeded, skip the navigate/close tail.
      if (attemptRef.current !== attempt) return;

      if (serverRedirect) {
        navigate(serverRedirect);
      } else if (localRedirect) {
        navigate(localRedirect);
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      if (attemptRef.current !== attempt) return; // closed mid-flight (#3264 P2)
      const apiError = err as ApiError;
      setError(apiError.message || 'Invalid code. Please try again.');
    } finally {
      if (attemptRef.current === attempt) setLoading(false);
    }
  };

  // Resend SMS code — must pass same invite/redirect context as initial request
  const handleResendCode = async () => {
    setCanResend(false);
    setError(null);
    const attempt = attemptRef.current; // #3264 P2
    try {
      let inviteCode: string | undefined;
      if (pendingNavigation?.startsWith('/invite/')) {
        inviteCode = pendingNavigation.split('/invite/')[1]?.split('?')[0];
      }
      await authApi.requestSmsCode(
        normalizedPhone,
        turnstileToken || undefined,
        inviteCode,
        toSafeRedirectPath(pendingNavigation) ?? undefined
      );
      setTimeout(() => {
        if (attemptRef.current === attempt) setCanResend(true);
      }, 30000);
    } catch (err) {
      if (attemptRef.current !== attempt) return; // closed mid-flight (#3264 P2)
      // #2930: a user can opt out (reply STOP) after the first code, then tap
      // Resend and hit the same dead end. Map SMS_OPTED_OUT to the same
      // recovery copy as the request path; otherwise the generic resend error.
      const apiError = err as ApiError;
      setError(
        apiError.code === 'SMS_OPTED_OUT'
          ? smsOptedOutRecoveryCopy(apiError)
          : 'Failed to resend code. Please try again.',
      );
      setCanResend(true);
    }
  };

  // #2557: the header subtitle tracks the sub-state the body already switches
  // on, instead of showing the identifier-entry instruction in every state
  // (including SMS verify and reviewer, where it contradicted the body below).
  // - identifier entry: the original instruction (nothing else states it).
  // - SMS verify (awaitingCode): name the masked destination — info the body's
  //   generic "sent to your phone" prompt lacks; native's login screen
  //   (mobile/app/(auth)/login.tsx) uses the same "we sent a code to <phone>"
  //   framing at this step, so web does not diverge.
  // - reviewer / success: the body prompt / success banner already carry the
  //   instruction, so the header stays quiet rather than repeating it (#2554:
  //   duplicated header copy is clutter).
  const headerSubtitle: string | null = success
    ? null
    : awaitingCode
      ? `We sent a code to ${maskPhoneE164(normalizedPhone)}.`
      : reviewerMode
        ? null
        : 'Enter your email or phone number to sign in.';

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      // #3324 (Codex F3): while the legal-doc dialog is open on top, make this
      // whole modal inert — no repo focus-trap primitive exists, and inert both
      // blocks Shift+Tab from reaching the form under aria-modal and hides it
      // from pointer/AT. The dialog is portaled to <body> (outside this tree),
      // so it stays interactive. React 19 renders the boolean prop correctly
      // (absent when false).
      inert={legalDoc !== null}
      role="dialog"
      aria-modal="true"
      // #2554: `aria-label`, not `aria-labelledby` — the visible "Login"
      // heading is gone (see the header below). APG's rule is labelledby when
      // a visible title exists, label when one does not. A `sr-only` h2 was
      // the alternative, rejected because its "not visible" half is unprovable
      // under jsdom: no Tailwind stylesheet loads there, so `sr-only` computes
      // to visible and the guard would pass whether or not the class survived.
      aria-label="Login"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm oro-fade-in"
        onClick={handleClose}
        aria-label="Close modal"
      />
      
      {/* Modal Content */}
      {/* The testid is the modal PANEL, deliberately — role="dialog" sits on the
          fixed inset-0 wrapper above, whose box is the whole viewport, so it is
          useless as a containment boundary in a layout test. The Card inside
          this div carries overflow-hidden and does the actual clipping, and it
          is w-full inside this fixed-width panel, so this box is the clipping
          box. Card takes no arbitrary props (see Card.tsx: children/variant/
          className only), so the handle goes here rather than on it. */}
      <div
        className="relative z-10 w-full max-w-sm oro-modal-enter"
        data-testid="login-modal-panel"
      >
        <Card className="border-oro-border shadow-xl shadow-black/50 bg-oro-surface overflow-hidden">
          {/* Environment warning for QA/Dev (#1448) — renders nothing on production */}
          <EnvironmentBanner className="-mx-4 md:-mx-5 -mt-4 md:-mt-5 mb-4" />
          {/* Header */}
          {/* #2553: the close button is positioned out of flow, NOT a flex
              sibling. As a sibling it forced `flex-1 mr-8` on the content
              column, so that column's centre sat (mr-8 + w-8) / 2 = 32px LEFT
              of the modal's — and `text-center` then faithfully centred the
              lockup, heading and subtitle on the wrong axis. The tell was that
              the subtitle shared the offset; a logo-only artwork problem would
              not have moved the text with it. Out of flow, the column spans the
              full width and the two axes coincide. */}
          <div className="relative mb-6">
            {/* px-10 reserves the close button's 32px on BOTH sides (40px, so
                8px of breathing room). Symmetric is the whole point: it keeps
                the column's centre on the modal's — the asymmetric `mr-8` is
                what caused #2553 — while still bounding the lockup so the
                out-of-flow button cannot land on the artwork.
                Needed because the button no longer reserves width: at the
                320px floor of the supported range (release-checklist.md:313,
                responsive-design/SKILL.md:80) the content box is 256px, and a
                centred 200px lockup left 28px gutters against a 32px button —
                a measured 4.9px overlap. Tailwind preflight's
                `img { max-width: 100% }` then lets the artwork shrink into the
                padded box instead of colliding. Caught by Frank on #2555;
                the e2e guard now runs the full 320-1280px range. */}
            <div className="text-center px-10">
              {/* #2523: horizontal, matching the native login screen (#2516).
                  No `rounded-xl` — the horizontal asset is trimmed to its ink
                  and transparent, so there is no plate to round. `size` is the
                  WIDTH here; height follows the artwork aspect. */}
              <OroLockup orientation="horizontal" size={200} className="mx-auto mb-3" />
              {/* #2538: no "ACCESS" eyebrow here. It predated the brand (03d1beca,
                  2025-12-02) and never fit the convention it looked like: ORO's
                  eyebrow is `text-xs tracking-[0.35em]` carrying a CONTEXTUAL
                  label (ClubMembersPage renders the club name in it). This one
                  was `text-sm tracking-[0.3em]` with the static word "Access" —
                  wrong metrics, and naming nothing.
                  #2554: the "Login" h2 that sat here is gone too — brand-owner
                  call, it said nothing the subtitle below does not, and it
                  separated the lockup from the instruction it belongs with.
                  Native's login (mobile/app/(auth)/login.tsx) already goes
                  lockup → content with nothing between, so this closes a
                  divergence. The dialog's accessible name moved to `aria-label`
                  on the wrapper above — do NOT delete that without replacing
                  it, or the dialog goes nameless. */}
              {headerSubtitle && (
                <p className="text-text-secondary">{headerSubtitle}</p>
              )}
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="absolute top-0 right-0 w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary transition-colors rounded-lg hover:bg-oro-surface-alt"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Success State */}
          {/* #1890: `success` is set only on the email and `!isPhone` waitlist
              paths, so this is always the email confirmation; the unreachable
              `isPhone ? 'Check your phone!'` arm was removed. */}
          {success && (
            <div className="mb-6 rounded-md border border-oro-success/40 bg-oro-success/10 p-4">
              <p className="text-sm text-oro-success text-center">
                Check your inbox! We sent you a magic link.
              </p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="mb-6 rounded-md border border-oro-error/40 bg-oro-error/10 p-4">
              <p className="text-sm text-oro-error text-center">
                {error}
              </p>
            </div>
          )}

          {/* Auth Loading State */}
          {authLoading && (
            <div className="mb-6 p-4 text-center">
              <div className="oro-loading h-8 w-8 rounded-full bg-oro-gold mx-auto mb-2"></div>
              <p className="text-text-secondary text-sm">Checking authentication...</p>
            </div>
          )}

          {/* Login Form */}
          {!authLoading && (<>
            <form onSubmit={handleSubmit} noValidate className="space-y-6">
              <div className="space-y-2">
                <label
                  htmlFor="identifier"
                  className="block text-sm font-medium tracking-wide text-text-secondary"
                >
                  {isPhone ? 'Phone number' : 'Email or phone'}
                </label>
                <Input
                  type={isPhone ? 'tel' : 'text'}
                  id="identifier"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  disabled={loading || success || reviewerMode}
                  placeholder="you@example.com or (555) 123-4567"
                  required
                  size="lg"
                  autoComplete={isPhone ? 'tel' : 'email'}
                />
                {isPhone && !awaitingCode && (
                  <p className="text-xs text-text-muted">
                    SMS login available for US and Canada numbers.
                  </p>
                )}
              </div>

              {/* Cloudflare Turnstile Bot Protection */}
              {turnstileSiteKey && !success && !reviewerMode && (
                <div className="flex justify-center">
                  <Turnstile
                    siteKey={turnstileSiteKey}
                    onSuccess={(token) => {
                      setTurnstileToken(token);
                      setTurnstileError(false);
                    }}
                    onError={() => {
                      setTurnstileError(true);
                      setTurnstileToken(null);
                    }}
                    onExpire={() => {
                      setTurnstileToken(null);
                    }}
                    options={{
                      theme: 'dark',
                      size: 'normal',
                      retry: 'auto',
                      appearance: 'always',
                    }}
                  />
                </div>
              )}
              
              {/* Turnstile Error Message */}
              {turnstileError && (
                <div className="rounded-md border border-oro-border bg-oro-surface-alt p-3">
                  <p className="text-xs text-text-secondary text-center">
                    Security verification failed. Please try refreshing the page.
                  </p>
                </div>
              )}
              
              {/* #3312: explicit Terms agreement, required before the first
                  sign-in on this device. Apple App Review (Guideline 1.2,
                  2026-09-03) asks that users "agree to terms", which #3262's
                  passive line presents but does not require.

                  Rendered INSIDE the form and above the submit button so the
                  control the user must act on sits next to the action it
                  gates. `needsTerms` is read once on mount, so this cannot
                  vanish mid-interaction.

                  The triggers do NOT need stopPropagation: a label's
                  activation behavior does nothing for events targeted at
                  interactive content descendants (a <button> is interactive
                  content), so opening a document cannot tick the box. Verified
                  by mutation — adding the guard changed no test, because there
                  was no bug to guard.

                  #3324: they are <button>s that open the document in a dialog
                  over this modal (setLegalDoc), NOT links that navigate away —
                  leaving would drop the identifier + this checkbox state, the
                  whole point of the Apple 1.2 "agree before sign-in" flow.
                  underline + font-medium so they read as links (owner ask). */}
              {needsTerms && !awaitingCode && !reviewerMode && (
                <div className="flex items-start gap-3">
                  <input
                    id="login-terms-agree"
                    type="checkbox"
                    checked={termsChecked}
                    onChange={(e) => setTermsChecked(e.target.checked)}
                    disabled={loading || success}
                    data-testid="login-terms-checkbox"
                    className="w-5 h-5 text-accent bg-oro-surface border-oro-border rounded mt-0.5 focus:ring-accent focus:ring-2 shrink-0"
                  />
                  <label
                    htmlFor="login-terms-agree"
                    className="text-sm leading-relaxed text-text-secondary cursor-pointer"
                  >
                    I agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setLegalDoc('terms')}
                      aria-haspopup="dialog"
                      aria-expanded={legalDoc === 'terms'}
                      className="underline font-medium hover:text-text-primary transition-colors"
                    >
                      Terms of Service
                    </button>{' '}
                    and{' '}
                    <button
                      type="button"
                      onClick={() => setLegalDoc('privacy')}
                      aria-haspopup="dialog"
                      aria-expanded={legalDoc === 'privacy'}
                      className="underline font-medium hover:text-text-primary transition-colors"
                    >
                      Privacy Policy
                    </button>
                    .
                  </label>
                </div>
              )}

              {!awaitingCode && !reviewerMode && (
                <Button
                  type="submit"
                  disabled={loading || success || termsBlocked}
                  loading={loading}
                  className="w-full"
                  size="lg"
                >
                  {isPhone ? 'Send Verification Code' : 'Send Login Link'}
                </Button>
              )}
            </form>

            {/* #3262: EULA presented before sign-in (Apple 1.2, #3257 F4).
                Inside the non-loading block but outside every sub-state
                conditional, so it survives the SMS-code and reviewer swaps.
                Styled per the SmsConsentDisclosure precedent (text-[11px]
                text-text-muted, underline).
                #3324: the links are <button>s that open the document in a
                dialog over this modal (setLegalDoc), NOT links that navigate
                away — the login flow (identifier state) stays in place.
                font-medium so the quiet line still reads its links as links
                (owner ask). */}
            {/* #3312: once this device has accepted, the checkbox above is
                gone and this quiet line is what remains — the #3262
                presentation still stands for every later sign-in, it just
                stops asking. */}
            {!needsTerms && (
            <p
              className="mt-4 text-[11px] leading-relaxed text-text-muted text-center"
              data-testid="login-terms-consent"
            >
              By continuing, you agree to the{' '}
              <button
                type="button"
                onClick={() => setLegalDoc('terms')}
                aria-haspopup="dialog"
                aria-expanded={legalDoc === 'terms'}
                className="underline font-medium hover:text-text-secondary transition-colors"
              >
                Terms of Service
              </button>{' '}
              and{' '}
              <button
                type="button"
                onClick={() => setLegalDoc('privacy')}
                aria-haspopup="dialog"
                aria-expanded={legalDoc === 'privacy'}
                className="underline font-medium hover:text-text-secondary transition-colors"
              >
                Privacy Policy
              </button>
              .
            </p>
            )}

            {/* #3126: the App Store affordance where the "sign in" instruction
                lands hardest. Only in the idle sign-in state — not during SMS
                code entry, reviewer login, or after a magic link is sent. Now a
                react-router <Link> to the /app landing page (the modal renders
                inside the app Router, so <Link> resolves); the store 302 (/ios)
                stays on the badge + FAQ.
                #3126 r1 (Frank Medium / George M1): call onClose UNCONDITIONALLY
                on click. This genuinely leaves the login flow, exactly like
                handleLegalNavigateAway above — so close this z-50 overlay before
                the route change or /app paints under it. NOT handleClose: that
                is loading-gated and would leave the overlay parked over /app if
                a submit is in flight, and the #3264 attemptRef guard already
                neutralises any in-flight tail when the modal closes mid-request. */}
            {!awaitingCode && !reviewerMode && !success && (
              <p className="mt-4 text-center text-sm text-text-muted">
                On an iPhone?{' '}
                <Link
                  to="/app"
                  onClick={onClose}
                  className="font-medium text-accent hover:text-accent-light transition-colors"
                >
                  Get the app
                </Link>
              </p>
            )}

            {/* SMS Code Verification (#1453) */}
            {awaitingCode && (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <p className="text-sm text-text-secondary text-center">
                  Enter the 6-digit code sent to your phone.
                </p>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  disabled={loading}
                  placeholder="000000"
                  autoFocus
                  size="lg"
                  className="text-center tracking-[0.5em] text-xl"
                />
                <Button
                  type="submit"
                  disabled={loading || verificationCode.length !== 6}
                  loading={loading}
                  className="w-full"
                  size="lg"
                >
                  Verify
                </Button>
                {canResend && (
                  <button
                    type="button"
                    onClick={handleResendCode}
                    className="w-full text-sm text-text-muted hover:text-accent transition-colors"
                  >
                    Resend code
                  </button>
                )}
              </form>
            )}

            {/* #2345: App Review demo-account static-code entry (email path) */}
            {reviewerMode && (
              <form onSubmit={handleReviewerLogin} className="space-y-4">
                <p className="text-sm text-text-secondary text-center">
                  Enter the review access code for this account.
                </p>
                <Input
                  type="text"
                  value={reviewerCode}
                  onChange={(e) => setReviewerCode(e.target.value)}
                  disabled={loading}
                  placeholder="Review code"
                  autoFocus
                  size="lg"
                  maxLength={256}
                  autoComplete="off"
                />
                <Button
                  type="submit"
                  disabled={loading || !reviewerCode.trim()}
                  loading={loading}
                  className="w-full"
                  size="lg"
                >
                  Sign In
                </Button>
              </form>
            )}
          </>)}
        </Card>
      </div>

      {/* #3324: the legal document dialog, portaled to <body> above this
          modal (z modal layer > overlay). Opening it keeps this modal mounted,
          so the identifier and Terms-checkbox state are preserved. */}
      <LegalDocModal
        doc={legalDoc}
        onClose={() => setLegalDoc(null)}
        onNavigateAway={handleLegalNavigateAway}
      />
    </div>
  );
}